import VenueBasedEventRepository from "@app-contexts/dashboard/features/events/data/repositories/VenueBasedEventRepository";
import { VenueBasedEventSerializer } from "@app-contexts/dashboard/features/events/data/types/events";
import PublicBaseEventRepository from "@app-contexts/public/features/event-detail/data/repositories/PublicBaseEventRepository";
import { ITEM_TYPES_MAP } from "@shared/constants/enums";
import { useAnalyticsContext } from "@shared/contexts/AnalyticsContext/useAnalyticsContext";
import useAnalytics from "@shared/lib/analytics/hooks/useAnalytics";
import { GtagItemType } from "@shared/lib/analytics/types/analytics-types";
import {
  PublicBaseEventSerializer,
  TicketTypeSerializer as PublicTicketTypeSerializer,
} from "@shared/modules/events/types/public";
import { UseModelServiceType } from "@shared/services/api/hooks/useModelService";
import { useEffect, useMemo } from "react";

export default function useItemGroupEvent(
  eventId: number,
  ticketTypeId: number | undefined,
  eventService: () => UseModelServiceType<
    PublicBaseEventRepository | VenueBasedEventRepository
  >,
  updateTrackingBasketData: ((input: GtagItemType) => void) | undefined,
  itemGroupId?: number
) {
  const { enableAnalytics } = useAnalyticsContext();
  const analytics = useAnalytics();

  const { useRetrieve } = eventService();
  const { data: eventData, isFetching } = useRetrieve(eventId, {
    options: {
      enabled: Boolean(eventId),
      refetchOnWindowFocus: false,
    },
  });

  const ticketTypes = useMemo(() => {
    return (
      (eventData as PublicBaseEventSerializer)?.ticket_types ||
      (eventData as VenueBasedEventSerializer)?.tickettype_set ||
      []
    );
  }, [eventData]);

  useEffect(() => {
    // propagates fetched data up to the public checkout context
    // so the data can be used for GTAG tracking
    // Note: updateTrackingBasketData is only used in the public checkout context
    if (
      eventData &&
      ticketTypes.length > 0 &&
      ticketTypeId &&
      updateTrackingBasketData
    ) {
      const ticketInBasket = (ticketTypes as PublicTicketTypeSerializer[]).find(
        (tt) => tt.id === ticketTypeId
      );

      if (ticketInBasket && enableAnalytics) {
        const trackingData = analytics.formatGtagTrackingData(
          Number(itemGroupId),
          eventData.name || "",
          ticketInBasket?.name || "",
          ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id
        );
        updateTrackingBasketData(trackingData);
      }
    }
  }, [eventData, ticketTypes, ticketTypeId, updateTrackingBasketData]);

  return { eventData, ticketTypes, isFetching };
}
